import Decimal from "decimal.js";
import { noop } from "lodash";
import {
  NumberFormatBase,
  type NumberFormatValues,
  useNumericFormat,
} from "react-number-format";

import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { type InputProps, Input as TextInput } from "../ui/input";

export type NumberTextInputProps = Omit<
  InputProps,
  "value" | "onChange" | "disabled" | "readOnly"
> & {
  onChange?: (value: number | null) => void;
  onBlur?: (
    value: number | null,
    e: React.FocusEvent<HTMLInputElement>,
  ) => void;
  value?: number | null;
  selectTextOnFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  suffix?: string;
  prefix?: string;
  disableFastCentInput?: boolean;
  disabled?: boolean | string | React.ReactNode;
  readOnly?: boolean | string | React.ReactNode;
};

export const NumberInput = forwardRef<HTMLInputElement, NumberTextInputProps>(
  (
    {
      placeholder,
      onFocus,
      onBlur,
      value: _value,
      onChange = noop,
      defaultValue,
      selectTextOnFocus = true,
      suffix,
      prefix,
      disableFastCentInput = false,
      onKeyDown = noop,
      readOnly,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const [originalValue, setOriginalValue] = useState<number | null>(null);
    const [value, setValue] = useState<number | null>(_value ?? null);

    const [stringValue, setStringValue] = useState<string>();
    const [fixedDecimalScale, setFixedDecimalScale] = useState<boolean>(true);

    useEffect(() => {
      setValue(_value ?? null);
    }, [_value]);

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (readOnly) {
        onBlur?.(value, e);
        return;
      }

      let finalNumber: number | null = value;

      const stringNumber = e.target.value;
      if (
        !disableFastCentInput &&
        originalValue === null &&
        finalNumber !== null &&
        !stringNumber.includes(",")
      ) {
        finalNumber = new Decimal(finalNumber).dividedBy(100).toNumber();
      }

      setFixedDecimalScale(true);
      onChange(finalNumber);
      onBlur?.(finalNumber, e);

      // Also update our local "value" state so the input re-renders with "finalNumber"
      setValue(finalNumber);
    };

    const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (readOnly) {
        onFocus?.(e);
        return;
      }

      e.target.select();

      setOriginalValue(_value ?? null);
      if (value === null) {
        // Start fresh => see raw digits while typing
        setFixedDecimalScale(false);
      } else {
        // Already has a value => keep the 2-decimal format
        setFixedDecimalScale(true);
      }

      onFocus?.(e);
    };

    const handleValueChange = (values: NumberFormatValues) => {
      // Skip value changes if readOnly
      if (readOnly) {
        return;
      }

      if (values.floatValue === undefined || values.value === "") {
        setFixedDecimalScale(false);
        setOriginalValue(null);
      }
      onChange(values.floatValue ?? null);
      setValue(values.floatValue ?? null);
      setStringValue(values.formattedValue);
    };

    // Helper to check if the user's key modifies the input text
    const isModifyingKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Single-character keys (letters, numbers, punctuation) are length 1
      // Backspace & Delete also modify input
      const { key } = e;
      const singleChar =
        key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;
      return singleChar || key === "Backspace" || key === "Delete";
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);

      if (readOnly) {
        return;
      }

      const input = e.currentTarget;

      const isEntireTextSelected =
        input.selectionStart === 0 && input.selectionEnd === input.value.length;

      if (isEntireTextSelected && isModifyingKey(e)) {
        // If user is about to type over the entire selected text, clear the input and remove the decimal formatting
        setFixedDecimalScale(false);
        handleValueChange({
          floatValue: undefined,
          formattedValue: "",
          value: "",
        });

        setOriginalValue(null);
      }
    };

    const { format, removeFormatting, ...numericProps } = useNumericFormat({
      getInputRef: ref,
      placeholder,
      onFocus: handleOnFocus,
      ...rest,
      readOnly: Boolean(readOnly),
      value: value ?? "",
      onValueChange: handleValueChange,
      onKeyDown: handleKeyDown,
      onBlur: handleOnBlur,
      customInput: TextInput,
      type: "text",
      suffix,
      prefix,
      thousandSeparator: false,
      decimalSeparator: ",",
      decimalScale: 2,
      fixedDecimalScale: fixedDecimalScale,
    });

    const customIsCharacterSame = useCallback((compareProps: any) => {
      const {
        currentValue,
        formattedValue,
        currentValueIndex,
        formattedValueIndex,
      } = compareProps;
      const normalize = (char: string) => (char === "Subtract" ? "-" : char);
      return (
        normalize(currentValue[currentValueIndex]) ===
        normalize(formattedValue[formattedValueIndex])
      );
    }, []);

    const element = (
      <NumberFormatBase
        {...numericProps}
        format={format}
        removeFormatting={removeFormatting}
        isCharacterSame={customIsCharacterSame}
        onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => {
          // On first enter, highlight the entire text
          if (selectTextOnFocus) {
            const isAlreadyFocused = document.activeElement === e.currentTarget;
            if (!isAlreadyFocused) {
              e.preventDefault();
              e.currentTarget.focus();
            }
          }
        }}
      />
    );

    const message = useMemo(() => {
      if (disabled) {
        return disabled;
      }
      if (readOnly) {
        return readOnly;
      }
      return "";
    }, [disabled, readOnly]);

    if (
      typeof disabled === "string" ||
      typeof readOnly === "string" ||
      typeof readOnly === "object" ||
      typeof disabled === "object"
    ) {
      return (
        <HoverCard openDelay={1000} closeDelay={0}>
          <HoverCardTrigger>{element}</HoverCardTrigger>
          {Boolean(disabled || readOnly) && (
            <HoverCardContent
              align="start"
              sideOffset={-5}
              className="w-fit max-w-[500px] p-3"
            >
              {typeof message === "string" ? (
                <p className="text-sm">{message}</p>
              ) : (
                message
              )}
            </HoverCardContent>
          )}
        </HoverCard>
      );
    }

    return element;
  },
);
